/**
 * Crazy Cribbage - Core Modular Package Entry
 */

export * from './scoring/ScoringConstants.js';
export { Card } from './models/Card.js';
export { Deck } from './models/Deck.js';
export { Hand } from './models/Hand.js';
export { Player, SEAT_COLORS } from './models/Player.js';
export { Roster } from './models/Roster.js';
export { CribbageScorer, combinations, isConsecutiveRun, isSpadeCard } from './scoring/CribbageScorer.js';
export * from './board/BoardGeometry.js';
export { BoardRenderer } from './board/BoardRenderer.js';
export { PegTracker } from './board/PegTracker.js';
export { GameState } from './game/GameState.js';
export { GameEngine } from './game/GameEngine.js';
export { PintsManager, PINTS_OPTIONS_DEFS } from './variants/PintsManager.js';
export { AdultManager, ADULT_OPTIONS_DEFS } from './variants/AdultManager.js';
export { ClashManager, CLASH_OBSTACLE_TYPES, WHEEL_SLOTS, SIGN_PRIORITY, SIGN_DEFS, CLASH_DEFAULTS_481, CLASH_DEFAULTS_241, CLASH_DEFAULTS_361, getClashDefaults, getSkunkDefaults } from './variants/ClashManager.js';
export { CribbageAI } from './ai/CribbageAI.js';
export * from './board/ObstacleGeometry.js';
export { AudioManager } from './audio/AudioManager.js';
export { NetworkManager, ROOM_CODE_CHARS } from './net/NetworkManager.js';
export { CameraManager } from './net/CameraManager.js';
export { ChatManager } from './net/ChatManager.js';
export { ModalManager } from './ui/ModalManager.js';
export { SettingsManager, THEMES, CARD_BACKS, BOARD_FINISHES, PEG_SKINS, DEFAULT_SETTINGS, CHECK_IDS } from './ui/SettingsManager.js';
export { UIController } from './ui/UIController.js';
export { PintsSettings, PINTS_DRINKING_DEFS } from './variants/PintsSettings.js';
export { AdultSettings, ADULT_OPTIONS_LIST } from './variants/AdultSettings.js';
export { ClashSettings } from './variants/ClashSettings.js';
export { ClashPlacementModal, buildClashItemMetadata, getJumpTriggerLength } from './variants/ClashPlacementModal.js';
export { ClashPresets, CLASH_PRESETS_481, CLASH_PRESET_KEYS, CLASH_CUSTOM_LAYOUTS_KEY } from './variants/ClashPresets.js';
export { PintsPresets, PINTS_PRESETS_121, PINTS_PRESET_KEYS, PINTS_CUSTOM_LAYOUTS_KEY } from './variants/PintsPresets.js';
export { AdultPresets, ADULT_PRESETS_121, ADULT_PRESET_KEYS, ADULT_CUSTOM_LAYOUTS_KEY } from './variants/AdultPresets.js';
export { ClashSandboxModal, BRAND_ITEM_PALETTES } from './variants/ClashSandboxModal.js';
export { LayoutSanitizer, MAX_LAYOUT_JSON_LENGTH, MAX_CUSTOM_LAYOUTS, MAX_ITEMS_PER_LAYOUT, MAX_NAME_LENGTH, ALLOWED_BOARD_TARGETS, BRAND_ALLOWED_TYPES, BRAND_GAME_TITLES, TYPE_ALIASES, SYSTEM_TYPE_METADATA } from './variants/LayoutSanitizer.js';
export { ShowModal } from './ui/ShowModal.js';
export { CelebrationFX } from './ui/CelebrationFX.js';
export { AchievementManager, BADGE_DEFINITIONS } from './achievements/AchievementManager.js';
export { LobbyController } from './ui/LobbyController.js';
export { ClashMiniGames, CLASH_SIGN_DEFS, CLASH_WHEEL_SLOTS } from './variants/ClashMiniGames.js';
export { TableRenderer } from './ui/TableRenderer.js';
export { DropdownHelpManager, DROPDOWN_DESCRIPTIONS } from './ui/DropdownHelpManager.js';
export { CoachController } from './ui/CoachController.js';
export { CrazyManager, CRAZY_OPTIONS_DEFS, getVariantRequiredPlayers, areVariantPlayerRequirementsCompatible, isPlayerCountAllowedForVariant } from './variants/CrazyManager.js';
export { CrazySettings } from './variants/CrazySettings.js';
export { CanadianDoublesModal } from './variants/CanadianDoublesModal.js';
export { DiscardTrainer } from './trainer/DiscardTrainer.js';
export { PeggingTrainer, PEGGING_CATEGORIES } from './trainer/PeggingTrainer.js';
export { CountingTrainer } from './trainer/CountingTrainer.js';
export { CoachPostMortem } from './coach/CoachPostMortem.js';
export { ReactionManager, REACTION_ITEMS, BANTER_ITEMS } from './ui/ReactionManager.js';
export { SeriesTracker, SERIES_MODES } from './series/SeriesTracker.js';
export { HandScoreTracker } from './stats/HandScoreTracker.js';
export { HandOfGloryCard } from './ui/HandOfGloryCard.js';
export { DailyPuzzleManager, PEGGING_SCENARIOS } from './trainer/DailyPuzzleManager.js';
export { TournamentManager, TOURNAMENT_FORMATS, TOURNAMENT_SERIES_MODES, DEFAULT_AVATARS } from './series/TournamentManager.js';
export { TournamentModal } from './ui/TournamentModal.js';
export { IndexedDBStorage, appStorage } from './storage/IndexedDBStorage.js';
export { Board3DCatalog, DEFAULT_3D_BOARDS } from './board/Board3DCatalog.js';
export { BoardRenderer3D } from './board/BoardRenderer3D.js';
export { BoardSelectorModal } from './ui/BoardSelectorModal.js';
export { CrazyCribbageApp } from './app/CrazyCribbageApp.js';
export { CardAssassinOverlay } from './ui/CardAssassinOverlay.js';
export { JailAnimationOverlay } from './ui/JailAnimationOverlay.js';
export { BarAnimationOverlay } from './ui/BarAnimationOverlay.js';
export { CannonAnimationOverlay } from './ui/CannonAnimationOverlay.js';
export { SpeedTrapAnimationOverlay } from './ui/SpeedTrapAnimationOverlay.js';
export { ScenicRouteAnimationOverlay } from './ui/ScenicRouteAnimationOverlay.js';
export { GameReplayManager, REPLAY_STORAGE_KEY, AUTO_RECORD_KEY } from './game/GameReplayManager.js';
export { ReplayModal } from './ui/ReplayModal.js';
export { AvatarManager, PRESET_AVATARS } from './ui/AvatarManager.js';
export { Profile, STORAGE_KEY_PROFILE, STORAGE_KEY_NAME, STORAGE_KEY_AVATAR, STORAGE_KEY_DAILY_PUZZLE, STORAGE_KEY_DISCARD_TRAINER, STORAGE_KEY_PEGGING_TRAINER, STORAGE_KEY_CUSTOM_3D_BOARDS, GAME_BRANDS } from './profile/Profile.js';
export { RoomCatalog3D } from './board/RoomCatalog3D.js';
export { CardMesh3D, CardEngine3D } from './board/CardMesh3D.js';
export { SeatManager3D, PlayerAvatar3D } from './board/SeatManager3D.js';
export { Room3DLoadingOverlay } from './ui/Room3DLoadingOverlay.js';
export { TraditionalCard } from './ui/TraditionalCard.js';
export { Sanitizer, sanitizeHtml, sanitizeUserInput, installInnerHtmlHook, installUserInputHook, initSanitizer, getDOMPurify } from './utils/Sanitizer.js';

